// ANETT HORVATH — Admin Extension Pages
// All new admin pages: house-control, content, media, brand-assets, notifications, orders, metrics

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/database';
import { SUPPORTED_LANGUAGES, ORDER_SOURCES, TIER_LABELS } from '../../config/house.config';
import type { OrderSource } from '../../config/house.config';
import { getHouseState, setHouseState, getHouseRuntimeRecord, HOUSE_PHASES, type HousePhase } from '../../modules/house-state';
import { getAllContent, upsertContent, deleteContent, type EditableContent } from '../../modules/content';
import { getAllBrandAssets, upsertBrandAsset, deleteBrandAsset, type BrandAsset } from '../../modules/brand-assets';
import { getAllNotifications, type Notification } from '../../modules/notifications';
import { computeMetrics, getLastMetrics, type HouseMetrics } from '../../modules/analytics';

// Shared mini-components
const Btn = ({ children, onClick, v = 'p' }: { children: React.ReactNode; onClick: () => void; v?: 'p' | 'd' | 'g' }) => {
  const c = v === 'p' ? 'bg-charcoal text-ivory hover:bg-charcoal/90' : v === 'd' ? 'bg-burgundy text-ivory hover:bg-burgundy/80' : 'text-charcoal/40 hover:text-charcoal border border-charcoal/10';
  return <button onClick={onClick} className={`px-4 py-2 text-[10px] tracking-widest uppercase font-sans transition-colors ${c}`}>{children}</button>;
};
const Inp = ({ label, value, onChange, type = 'text' }: { label: string; value: any; onChange: (v: any) => void; type?: string }) => (
  <div className="space-y-1">
    <label className="text-[10px] tracking-widest uppercase text-charcoal/40 font-sans">{label}</label>
    {type === 'textarea' ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory resize-none" />
    ) : (
      <input type={type} value={value} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory" />
    )}
  </div>
);

// HOUSE CONTROL
export function HouseControlAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const adminId = store.session?.user_id || '';
  const [phase, setPhase] = useState<HousePhase>(getHouseState());
  const record = getHouseRuntimeRecord();

  const handleSave = () => {
    setHouseState(phase, adminId);
    store._audit(adminId, 'house_state_changed', 'house_runtime', 'global', { state: getHouseState() }, { state: phase });
    store._sysEvent('house_state_changed', { from: getHouseState(), to: phase });
    store._persist();
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-xl">House Control</h2>
      <div className="border border-charcoal/5 p-6 space-y-4">
        <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Current Phase</p>
        <div className="flex flex-wrap gap-2">
          {HOUSE_PHASES.map(p => (
            <button key={p} onClick={() => setPhase(p)}
              className={`px-4 py-2 text-[11px] tracking-widest uppercase font-sans transition-colors ${phase === p ? 'bg-charcoal text-ivory' : 'border border-charcoal/10 text-charcoal/40 hover:text-charcoal'}`}>
              {p.replace('_', ' ')}
            </button>
          ))}
        </div>
        <Btn onClick={handleSave}>{t('admin.save')}</Btn>
        {record && (
          <p className="text-[10px] text-charcoal/30 font-sans">Last updated: {new Date(record.updated_at).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
}

// CONTENT EDITOR
export function ContentAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState<EditableContent[]>(getAllContent());
  const [editing, setEditing] = useState<EditableContent | null>(null);
  const [key, setKey] = useState('');
  const [trans, setTrans] = useState<Record<string, { text: string }>>({});

  const startEdit = (item: EditableContent | null) => {
    if (item) { setKey(item.content_key); setTrans(item.translations); setEditing(item); }
    else { setKey(''); setTrans({}); setEditing({} as any); }
  };

  const save = () => {
    upsertContent(editing?.id || null, key, trans);
    setItems(getAllContent());
    setEditing(null);
  };

  const del = (id: string) => { deleteContent(id); setItems(getAllContent()); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl">{editing.id ? 'Edit Content' : 'New Content'}</h2>
          <Btn onClick={() => setEditing(null)} v="g">{t('admin.back')}</Btn>
        </div>
        <Inp label="Content Key" value={key} onChange={setKey} />
        <div className="space-y-2">
          {SUPPORTED_LANGUAGES.map(lang => (
            <div key={lang} className="border border-charcoal/5 p-3 space-y-1">
              <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">{lang.toUpperCase()}</p>
              <Inp label="Text" value={trans[lang]?.text || ''} onChange={v => setTrans({ ...trans, [lang]: { text: v } })} type="textarea" />
            </div>
          ))}
        </div>
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl">{t('admin.title')} — Content</h2>
        <Btn onClick={() => startEdit(null)}>{t('admin.create')}</Btn>
      </div>
      {items.map(c => (
        <div key={c.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div>
            <p className="font-mono text-sm">{c.content_key}</p>
            <p className="text-[10px] text-charcoal/30 font-sans">{c.translations.en?.text?.substring(0, 60) || '—'}...</p>
          </div>
          <div className="flex gap-2">
            <Btn onClick={() => startEdit(c)} v="g">{t('admin.edit')}</Btn>
            <Btn onClick={() => del(c.id)} v="d">{t('admin.delete')}</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

// BRAND ASSETS ADMIN
export function BrandAssetsAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState<BrandAsset[]>(getAllBrandAssets());
  const [editing, setEditing] = useState<BrandAsset | null>(null);
  const [key, setKey] = useState('');
  const [url, setUrl] = useState('');
  const [desc, setDesc] = useState('');

  const startEdit = (item: BrandAsset | null) => {
    if (item) { setKey(item.asset_key); setUrl(item.media_url); setDesc(item.description); setEditing(item); }
    else { setKey(''); setUrl(''); setDesc(''); setEditing({} as any); }
  };

  const save = () => {
    upsertBrandAsset(editing?.id || null, key, url, desc);
    setItems(getAllBrandAssets());
    setEditing(null);
  };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl">{editing.id ? 'Edit Asset' : 'New Asset'}</h2>
          <Btn onClick={() => setEditing(null)} v="g">{t('admin.back')}</Btn>
        </div>
        <Inp label="Asset Key" value={key} onChange={setKey} />
        <Inp label="Image URL" value={url} onChange={setUrl} />
        <Inp label="Description" value={desc} onChange={setDesc} />
        {url && <img src={url} alt={desc} className="max-w-xs border border-charcoal/5" onError={e => (e.currentTarget.style.display = 'none')} />}
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Brand Assets</h2>
        <Btn onClick={() => startEdit(null)}>{t('admin.create')}</Btn>
      </div>
      {items.map(a => (
        <div key={a.id} className="border border-charcoal/5 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {a.media_url && <img src={a.media_url} alt={a.description} className="w-10 h-10 object-contain" onError={e => (e.currentTarget.style.display = 'none')} />}
            <div>
              <p className="font-mono text-sm">{a.asset_key}</p>
              <p className="text-[10px] text-charcoal/30 font-sans">{a.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Btn onClick={() => startEdit(a)} v="g">{t('admin.edit')}</Btn>
            <Btn onClick={() => { deleteBrandAsset(a.id); setItems(getAllBrandAssets()); }} v="d">{t('admin.delete')}</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

// MEDIA ADMIN (extended)
export function MediaAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const adminId = store.session?.user_id || '';
  const assets = store.media_assets.filter(a => !a.deleted_at);
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');

  const addMedia = () => {
    if (!url) return;
    const id = crypto.randomUUID();
    const asset = { id, file_url: url, alt_text: alt || 'media', width: 0, height: 0, created_at: new Date().toISOString(), deleted_at: null };
    useStore.setState(s => ({ media_assets: [...s.media_assets, asset] }));
    store._audit(adminId, 'media_created', 'media_asset', id, null, { url });
    store._persist();
    setUrl(''); setAlt('');
  };

  const deleteMedia = (id: string) => {
    useStore.setState(s => ({ media_assets: s.media_assets.map(a => a.id === id ? { ...a, deleted_at: new Date().toISOString() } : a) }));
    store._audit(adminId, 'media_deleted', 'media_asset', id, null, null);
    store._persist();
  };

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">Media Assets</h2>
      <div className="border border-charcoal/5 p-4 space-y-2">
        <Inp label="Image URL" value={url} onChange={setUrl} />
        <Inp label="Alt Text" value={alt} onChange={setAlt} />
        <Btn onClick={addMedia}>{t('admin.create')}</Btn>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {assets.map(a => (
          <div key={a.id} className="border border-charcoal/5 p-2 space-y-1">
            <img src={a.file_url} alt={a.alt_text} className="w-full h-20 object-cover" onError={e => (e.currentTarget.style.display = 'none')} />
            <p className="text-[10px] text-charcoal/30 font-sans truncate">{a.alt_text}</p>
            <Btn onClick={() => deleteMedia(a.id)} v="d">{t('admin.delete')}</Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// NOTIFICATIONS ADMIN
export function NotificationsAdmin() {
  const notifs = getAllNotifications().slice(0, 50);
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">Notifications</h2>
      {notifs.length === 0 && <p className="text-sm text-charcoal/50 font-sans">No notifications yet.</p>}
      {notifs.map(n => (
        <div key={n.id} className="border-b border-charcoal/5 py-2 text-[11px] font-sans text-charcoal/60">
          <span className="text-charcoal/30">{new Date(n.created_at).toLocaleString()}</span> · <span className="font-medium text-charcoal">{n.type}</span> · {n.status} · {n.member_id.substring(0, 8)}
          {n.read_at ? ' · Read' : ' · Unread'}
        </div>
      ))}
    </div>
  );
}

// ORDERS ADMIN
export function OrdersAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const orders = store.orders.filter(o => !o.deleted_at).sort((a, b) => b.created_at.localeCompare(a.created_at));
  const adminId = store.session?.user_id || '';

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">{t('admin.title')} — Orders</h2>
      {orders.length === 0 && <p className="text-sm text-charcoal/50 font-sans">No orders.</p>}
      {orders.map(o => {
        const member = store.members.find(m => m.id === o.member_id);
        return (
          <div key={o.id} className="border border-charcoal/5 p-3 space-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm">{o.order_number}</p>
                <p className="text-[10px] text-charcoal/30 font-sans">{member?.first_name || 'Unknown'} · €{o.amount_eur} · {o.source} · {o.status}</p>
              </div>
              <div className="flex gap-2">
                {o.status === 'pending' && <Btn onClick={() => store.payOrder(o.id, adminId)} v="g">Pay</Btn>}
                {o.status === 'paid' && <Btn onClick={() => store.refundOrder(o.id, adminId)} v="d">Refund</Btn>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// METRICS ADMIN
export function MetricsAdmin() {
  const store = useStore();
  const [metrics, setMetrics] = useState<HouseMetrics | null>(getLastMetrics());

  const refresh = () => { setMetrics(computeMetrics(store)); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl">House Metrics</h2>
        <Btn onClick={refresh} v="g">Refresh</Btn>
      </div>
      {!metrics ? (
        <p className="text-sm text-charcoal/50 font-sans">Click refresh to compute metrics.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border border-charcoal/5 p-4 text-center">
            <p className="font-serif text-2xl">{metrics.active_members}</p>
            <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Active</p>
          </div>
          <div className="border border-charcoal/5 p-4 text-center">
            <p className="font-serif text-2xl">{metrics.waitlist_count}</p>
            <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Waitlist</p>
          </div>
          <div className="border border-charcoal/5 p-4 text-center">
            <p className="font-serif text-2xl">{metrics.total_orders}</p>
            <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Orders</p>
          </div>
          <div className="border border-charcoal/5 p-4 text-center">
            <p className="font-serif text-2xl">€{metrics.total_revenue_eur.toLocaleString()}</p>
            <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Revenue</p>
          </div>
          <div className="border border-charcoal/5 p-4 text-center">
            <p className="font-serif text-2xl">{metrics.avg_approval_days}d</p>
            <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Avg Approval</p>
          </div>
          {Object.entries(metrics.tier_distribution).map(([tier, count]) => (
            <div key={tier} className="border border-charcoal/5 p-4 text-center">
              <p className="font-serif text-2xl">{count}</p>
              <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">{TIER_LABELS[tier as keyof typeof TIER_LABELS] || tier}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
